import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Field, Modal, Spinner, Dot, Textarea, Select } from '../ui.jsx';

export function Integrations({ user, status, refreshStatus, toast }) {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModal, setAuthModal] = useState(false);
  const [connections, setConnections] = useState({});

  const loadBilling = () => api('/api/integrations/billing/status').then(setBilling).catch(()=>{}).finally(()=>setLoading(false));
  const loadConnections = () => api('/api/integrations/connections').then(setConnections).catch(()=>{});
  useEffect(() => { loadBilling(); loadConnections(); }, []);

  const connect = async (provider) => {
    try {
      const { url } = await api(`/api/integrations/${provider}/connect`);
      window.open(url, '_blank');
      // Re-poll for connection status periodically
      const t = setInterval(() => loadConnections(), 1500);
      setTimeout(() => clearInterval(t), 30000);
    } catch(e) { toast(e.message,'error'); }
  };
  const disconnect = async (provider) => {
    if (!confirm('Disconnect?')) return;
    try { await api(`/api/integrations/${provider}/disconnect`, { method:'POST' }); loadConnections(); toast('Disconnected'); }
    catch(e) { toast(e.message,'error'); }
  };

  const connectStripe = async () => {
    try { const { url } = await api('/api/integrations/stripe/connect'); window.open(url, '_blank'); }
    catch(e) { toast(e.message,'error'); }
  };
  const disconnectStripe = async () => {
    if (!confirm('Disconnect Stripe? Your payment links will stop working.')) return;
    try { await api('/api/integrations/stripe/disconnect', { method:'POST' }); refreshStatus(); toast('Stripe disconnected'); }
    catch(e) { toast(e.message,'error'); }
  };
  const provisionTwilio = async () => {
    const areaCode = prompt('Area code for your business number? (e.g. 813)', '813');
    if (!areaCode) return;
    try { const r = await api('/api/integrations/twilio/provision', { method:'POST', body:{ areaCode } }); refreshStatus(); toast(`Provisioned ${r.phone || 'mock number — set TWILIO_ACCOUNT_SID env var to enable real provisioning'}`); }
    catch(e) { toast(e.message,'error'); }
  };
  const connectQB = async () => {
    try { const { url } = await api('/api/integrations/quickbooks/connect'); window.open(url, '_blank'); }
    catch(e) { toast(e.message,'error'); }
  };
  // Note: Stripe is handled by the dedicated StripeOnboardingCard above (Express onboarding,
  // no OAuth). ElevenLabs is handled by ElevenLabsCard. Keep this grid focused on the rest.
  const cards = [
    { key:'twilio', name:'Twilio', desc:'Get a dedicated business number. SMS to leads, customers, and team.',
      connected: !!status?.twilio?.connected, sub: status?.twilio?.connected ? status.twilio.phone : 'Not provisioned',
      action: status?.twilio?.connected ? null : { label:'Provision Number', fn: provisionTwilio, v:'primary' },
      icon: '📱', color: G.cyan,
    },
    { key:'quickbooks', name:'QuickBooks Online', desc:'Sync paid invoices and expenses to QuickBooks automatically.',
      connected: !!status?.quickbooks?.connected, sub: status?.quickbooks?.connected ? 'Connected' : 'Not connected',
      action: status?.quickbooks?.connected ? { label:'Disconnect', fn: ()=>api('/api/integrations/quickbooks/disconnect',{method:'POST'}).then(refreshStatus), v:'danger' } : { label:'Connect QuickBooks', fn: connectQB, v:'primary' },
      icon: '📊', color: G.green },
  ];

  return <div style={{ padding:24 }}>
    <h1 style={{ margin:'0 0 20px 0', fontSize:24, fontWeight:800 }}>Integrations</h1>

    {/* Billing Authorization */}
    <div style={{ ...styles.card, padding:24, marginBottom:24, borderColor: billing?.billingAuthorized ? G.green+'66' : G.yellow+'66' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:14, flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:280 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
            <span style={{ fontSize:24 }}>🔐</span>
            <h2 style={{ margin:0, fontSize:20 }}>Billing Authorization</h2>
            {billing?.billingAuthorized ? <Badge c={G.green} bg={G.greenLight}><Dot c={G.green}/> Authorized</Badge>
              : <Badge c={G.yellow} bg={G.yellowLight}><Dot c={G.yellow} pulse/> Required</Badge>}
          </div>
          <div style={{ color:G.textMuted, fontSize:14, lineHeight:1.6 }}>
            Authorize TradeOps to (1) collect a small platform fee on every customer payment processed through Stripe Connect,
            (2) auto-bill your saved card or bank for the monthly subscription + Twilio overage, and
            (3) provision Twilio numbers under our master account on your behalf.
          </div>
          {billing && <div style={{ marginTop:14, display:'flex', gap:18, flexWrap:'wrap', fontSize:13 }}>
            <div><strong>Plan:</strong> <span style={{ color:G.purple }}>{billing.plan}</span></div>
            <div><strong>Platform fee:</strong> {(billing.feeBpsByPlan?.[billing.plan]||50)/100}% of customer payments</div>
            <div><strong>SMS:</strong> {billing.perSmsCents}¢/msg</div>
            <div><strong>Voice:</strong> {billing.perMinuteVoiceCents}¢/min</div>
          </div>}
        </div>
        <Btn v={billing?.billingAuthorized?'green':'primary'} onClick={()=>setAuthModal(true)}>
          {billing?.billingAuthorized ? 'View / Re-Authorize' : 'Review & Authorize'}
        </Btn>
      </div>
    </div>

    {user?.role === 'super_admin' && <PlatformSecretsCard toast={toast}/>}

    <StripeOnboardingCard toast={toast} refreshStatus={refreshStatus}/>

    <StripeWebhookCard toast={toast} user={user}/>

    <ElevenLabsCard toast={toast}/>

    {/* QuickBooks 2-way sync controls — only when QB is connected */}
    {status?.quickbooks?.connected && <QuickBooksSyncPanel toast={toast}/>}

    {/* Integration cards */}
    {loading ? <Spinner/> : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))', gap:14 }}>
      {cards.map(c => (
        <div key={c.key} style={{ ...styles.card, borderColor: c.connected ? c.color+'66' : G.cardBorder }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <div style={{ width:42, height:42, borderRadius:10, background:c.color+'22', color:c.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>{c.icon}</div>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <strong style={{ fontSize:16 }}>{c.name}</strong>
                {c.connected ? <Badge c={G.green} bg={G.greenLight}><Dot c={G.green}/> Connected</Badge>
                             : <Badge c={G.textMuted} bg={G.input}>Not connected</Badge>}
              </div>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{c.sub}</div>
            </div>
          </div>
          <div style={{ fontSize:13, color:G.textMuted, lineHeight:1.5, marginBottom:12 }}>{c.desc}</div>
          {c.action && <Btn v={c.action.v} onClick={c.action.fn}>{c.action.label}</Btn>}
        </div>
      ))}
    </div>}

    {/* Calendars + Ad accounts */}
    <h2 style={{ margin:'30px 0 14px 0', fontSize:18 }}>Calendars + Ad Accounts</h2>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:14 }}>
      {Object.entries(connections).filter(([key,c]) => ['gcal','outlook','google_ads','google_lsa','meta_ads','tiktok_ads','gsc','gbp'].includes(key)).map(([key,c]) => {
        const icon = key==='gcal'?'📅':key==='outlook'?'📅':key==='google_ads'?'🔍':key==='google_lsa'?'📍':key==='meta_ads'?'📘':key==='tiktok_ads'?'🎵':key==='gsc'?'📊':key==='gbp'?'🗺️':'🔌';
        const color = key==='gcal'?G.cyan:key==='outlook'?G.blue:key==='google_ads'?G.green:key==='google_lsa'?G.yellow:key==='meta_ads'?G.purple:key==='tiktok_ads'?G.pink:key==='gsc'?G.cyan:key==='gbp'?G.green:G.textMuted;
        const setupDocs = {
          gcal: 'https://console.cloud.google.com/apis/credentials',
          outlook: 'https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade',
          google_ads: 'https://console.cloud.google.com/apis/credentials',
          google_lsa: 'https://ads.google.com/local-services-ads/',
          meta_ads: 'https://developers.facebook.com/apps',
          tiktok_ads: 'https://business-api.tiktok.com/portal/apps',
          gsc: 'https://search.google.com/search-console',
          gbp: 'https://business.google.com',
        };
        const setupEnvVar = {
          gcal: 'GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET',
          outlook: 'MS_CLIENT_ID + MS_CLIENT_SECRET',
          google_ads: 'GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET',
          google_lsa: 'GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET',
          meta_ads: 'META_APP_ID + META_APP_SECRET',
          tiktok_ads: 'TIKTOK_APP_ID + TIKTOK_APP_SECRET',
          gsc: 'GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET',
          gbp: 'GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET',
        };
        const isConfigured = !!c.configured;
        const isAdsFamily = key === 'google_ads' || key === 'google_lsa';
        // Token-missing notice is platform-admin only. Regular clients
        // shouldn't see it — they just connect their account; the platform
        // admin handles the developer-token side.
        const adsTokenMissing = isAdsFamily && c.developerTokenConfigured === false && user?.role === 'super_admin';
        return <div key={key} style={{ ...styles.card, borderColor: c.connected ? color+'66' : G.cardBorder }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <div style={{ width:42, height:42, borderRadius:10, background:color+'22', color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>{icon}</div>
            <div style={{ flex:1 }}>
              <strong style={{ fontSize:15 }}>{c.label}</strong>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:2, flexWrap:'wrap' }}>
                {c.connected ? <Badge c={G.green} bg={G.greenLight}><Dot c={G.green}/> {c.identity || 'Connected'}</Badge>
                  : !isConfigured ? <Badge c={G.yellow} bg={G.yellowLight}>Setup required (admin)</Badge>
                  : <Badge c={G.textMuted} bg={G.input}>Not connected</Badge>}
                {adsTokenMissing && <Badge c={G.yellow} bg={G.yellowLight}>Developer token missing</Badge>}
              </div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:4 }}>{c.scope === 'calendar' ? 'Per-user · jobs sync to your calendar' : 'Per-account · ad spend + lead data syncs to Marketing'}</div>
            </div>
          </div>
          {adsTokenMissing && (
            <div style={{ fontSize:11, color:G.textMuted, background:G.yellow+'15', border:`1px solid ${G.yellow}44`, borderRadius:6, padding:'8px 10px', marginBottom:10, lineHeight:1.5 }}>
              Platform admin: this client can connect their {key === 'google_lsa' ? 'Local Services Ads' : 'Google Ads'} account here, but no data will sync until <code style={{ color:G.cyan }}>GOOGLE_ADS_DEVELOPER_TOKEN</code> is set in Fly secrets. Apply at <a href="https://ads.google.com" target="_blank" style={{ color:G.purple }}>ads.google.com → Tools → API Center ↗</a>.
            </div>
          )}
          {c.connected
            ? <Btn sz="sm" v="danger" onClick={()=>disconnect(key)}>Disconnect</Btn>
            : isConfigured
              ? <Btn sz="sm" v="primary" onClick={()=>connect(key)}>Connect {c.label}</Btn>
              : <div style={{ fontSize:11, color:G.textMuted, lineHeight:1.5 }}>
                  Set <code style={{ color:G.cyan }}>{setupEnvVar[key]}</code> in platform env.{' '}
                  <a href={setupDocs[key]} target="_blank" style={{ color:G.purple }}>Create OAuth app ↗</a>
                </div>}
        </div>;
      })}
    </div>

    {/* Social Posting — hidden from sub-accounts until Meta/Google/TikTok app reviews
        complete. Super-admins always see it for setup + testing. */}
    {user?.role === 'super_admin' && <SocialPostingCard toast={toast}/>}

    {user?.role === 'super_admin' && <RankAndRentApiKeysCard toast={toast}/>}

    <CalendarConnectorsCard toast={toast} status={status} refreshStatus={refreshStatus}/>

    <LeadCaptureCard toast={toast}/>

    <FinancingPartnersCard toast={toast}/>

    <BookingWidgetCard toast={toast}/>

    <TollFreeVerificationCard toast={toast}/>

    <SmsCompliancePanel toast={toast}/>

    <BillingAuthModal open={authModal} onClose={()=>setAuthModal(false)} billing={billing} user={user} onAuthed={()=>{ loadBilling(); setAuthModal(false); toast('Billing authorized'); }}/>
  </div>;
}

function StripeOnboardingCard({ toast, refreshStatus }) {
  const [s, setS] = useState(null);
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([
    api('/api/integrations/stripe/detailed-status').then(setS).catch(()=>setS({ connected:false })),
    api('/api/integrations/billing/status').then(setBilling).catch(()=>setBilling({ billingAuthorized:false })),
  ]);
  useEffect(() => { load(); }, []);
  if (!s) return null;

  // GATE: Stripe Connect setup requires billing authorization first
  const billingAuthorized = !!billing?.billingAuthorized;

  const start = async () => {
    if (!billingAuthorized) {
      toast('Authorize TradeOps billing first (top of this page) before connecting Stripe', 'error');
      return;
    }
    setBusy(true);
    try {
      const { url } = await api('/api/integrations/stripe/express-onboarding', { method:'POST' });
      if (url) window.location.href = url;
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const openDashboard = async () => {
    try {
      const { url } = await api('/api/integrations/stripe/dashboard-link');
      if (url) window.open(url, '_blank');
    } catch (e) { toast(e.message, 'error'); }
  };
  const refresh = async () => { await load(); if (refreshStatus) refreshStatus(); toast('Status refreshed'); };

  const state = !s.connected ? 'not_started'
    : s.complete ? 'live'
    : 'in_progress';
  const c = state === 'live' ? G.green : state === 'in_progress' ? G.yellow : G.purple;

  return <div style={{ ...styles.card, padding:24, marginBottom:24, borderColor: c+'66' }}>
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:14, flexWrap:'wrap' }}>
      <div style={{ flex:1, minWidth:280 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
          <span style={{ fontSize:24 }}>💳</span>
          <h2 style={{ margin:0, fontSize:20 }}>Stripe Payments</h2>
          <Badge c={c} bg={c+'22'}>
            <Dot c={c} pulse={state==='in_progress'}/>{' '}
            {state === 'live' ? 'Live · Accepting payments' : state === 'in_progress' ? 'Setup incomplete' : 'Not started'}
          </Badge>
        </div>
        {state === 'not_started' && <div style={{ color:G.textMuted, fontSize:14, lineHeight:1.6 }}>
          Connect a Stripe Express account to collect customer payments on estimates and invoices. Money lands directly in your bank account — TradeOps charges a small platform fee per transaction.
        </div>}
        {state === 'in_progress' && <>
          <div style={{ color:G.textMuted, fontSize:14, marginBottom:8 }}>Stripe still needs a few details before they'll release payouts.</div>
          {(s.requirementsDue || []).length > 0 && <div style={{ fontSize:12, marginTop:6 }}>
            <strong>Still needed:</strong>{' '}
            <span style={{ color:G.textMuted }}>{(s.requirementsDue||[]).slice(0,5).map(prettyReq).join(' · ')}</span>
          </div>}
        </>}
        {state === 'live' && <div style={{ color:G.textMuted, fontSize:14 }}>
          You're set up to accept cards + bank transfers. Manage payouts, refunds, and disputes from your Stripe dashboard.
        </div>}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {state === 'not_started' && (billingAuthorized
          ? <Btn v="primary" onClick={start} disabled={busy}>{busy ? <Spinner/> : 'Start Stripe Setup'}</Btn>
          : <div style={{ padding:'10px 14px', background:G.yellowLight, border:`1px solid ${G.yellow}66`, borderRadius:8, fontSize:12, color:G.yellow, maxWidth:280, lineHeight:1.4 }}>
              🔒 Authorize TradeOps billing first (Billing Authorization card at top of page) before connecting Stripe.
            </div>)}
        {state === 'in_progress' && <>
          <Btn v="primary" onClick={start} disabled={busy}>{busy ? <Spinner/> : 'Continue Setup'}</Btn>
          <Btn v="ghost" sz="sm" onClick={refresh}>Refresh status</Btn>
        </>}
        {state === 'live' && <>
          <Btn v="ghost" onClick={openDashboard}>Open Stripe Dashboard ↗</Btn>
          <Btn v="ghost" sz="sm" onClick={refresh}>Refresh status</Btn>
        </>}
      </div>
    </div>
  </div>;
}

function CloneVoiceWidget({ toast, onCloned }) {
  const [name, setName] = useState('');
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chunks, setChunks] = useState([]);
  const recRef = React.useRef(null);
  const fileRef = React.useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const local = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) local.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(local, { type: 'audio/webm' });
        await uploadBlob(blob);
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) { toast('Microphone access denied: ' + e.message, 'error'); }
  };
  const stopRecording = () => {
    recRef.current?.stop();
    setRecording(false);
  };

  const uploadBlob = async (blob) => {
    if (!name.trim()) return toast('Give the voice a name first', 'error');
    setBusy(true);
    try {
      const audioBase64 = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.readAsDataURL(blob);
      });
      const result = await api('/api/integrations/elevenlabs/clone-voice', {
        method: 'POST',
        body: { name: name.trim(), audioBase64, mimeType: blob.type, description: 'Cloned from owner audio' },
      });
      onCloned(result.voiceId);
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); setChunks([]); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadBlob(file);
  };

  return <div style={{ marginTop:16, paddingTop:16, borderTop:`1px solid ${G.border}` }}>
    <div style={{ fontSize:14, fontWeight:700, marginBottom:6 }}>🎤 Clone Your Voice</div>
    <div style={{ fontSize:12, color:G.textMuted, marginBottom:10, lineHeight:1.5 }}>
      Record 1-3 minutes of you speaking naturally (or upload an existing MP3/WAV). ElevenLabs creates an AI version of your voice that the agents use on outbound calls. Best results: quiet room, varied tone, no background music.
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:8, alignItems:'end' }}>
      <Field label="Voice name (e.g. 'Mike — Arctic HVAC')">
        <Input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name + business"/>
      </Field>
      {!recording
        ? <Btn v="primary" onClick={startRecording} disabled={busy}>🔴 Record</Btn>
        : <Btn v="danger" onClick={stopRecording}>⏹ Stop + Upload</Btn>}
      <Btn v="ghost" onClick={()=>fileRef.current?.click()} disabled={busy || recording}>📁 Upload file</Btn>
      <input ref={fileRef} type="file" accept="audio/*" style={{ display:'none' }} onChange={handleFile}/>
    </div>
    {busy && <div style={{ marginTop:8, color:G.cyan, fontSize:13 }}><Spinner sz={14}/> Uploading & training…</div>}
  </div>;
}

function prettyReq(r) {
  const map = {
    'external_account': 'Bank account',
    'individual.id_number': 'SSN/Tax ID',
    'tos_acceptance.date': 'Accept Stripe terms',
    'business_profile.url': 'Website URL',
    'business_profile.mcc': 'Business category',
    'individual.verification.document': 'ID document',
    'individual.address': 'Personal address',
    'individual.dob': 'Date of birth',
  };
  return map[r] || r.replace(/_/g,' ');
}

function ElevenLabsCard({ toast }) {
  const [s, setS] = useState(null);
  const [phones, setPhones] = useState([]);
  const [voices, setVoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = () => api('/api/integrations/elevenlabs/status').then(setS).catch(()=>setS({ connected:false }));
  useEffect(() => { load(); }, []);

  const loadDetails = async () => {
    try {
      const [p, v] = await Promise.all([
        api('/api/integrations/elevenlabs/phones').catch(()=>({phones:[]})),
        api('/api/integrations/elevenlabs/voices').catch(()=>({voices:[]})),
      ]);
      setPhones(p.phones||[]); setVoices(v.voices||[]);
    } catch {}
  };

  const importNumber = async () => {
    setBusy(true);
    try {
      const r = await api('/api/integrations/elevenlabs/import', { method:'POST' });
      console.log('[elevenlabs import] response:', r);
      const id = r?.phoneId || r?.phone_number_id || r?.id;
      if (!id) { toast(`Import returned no phone ID — ${JSON.stringify(r).slice(0,200)}`, 'error'); return; }
      let msg = `Imported number to ElevenLabs (phone_id: ${String(id).slice(0,16)}…)`;
      if (r.agentId) msg += `. Agent ${String(r.agentId).slice(0, 14)}… assigned for inbound.`;
      if (r.assignError) msg += ` ⚠ Agent assign failed: ${r.assignError}`;
      toast(msg, r.assignError ? 'warning' : 'success');
      await load(); await loadDetails();
    } catch (e) {
      console.error('[elevenlabs import] failed:', e);
      toast(`Import failed: ${e.message || 'unknown error'}`, 'error');
    }
    finally { setBusy(false); }
  };

  const setPhone = async (phoneId) => {
    await api('/api/integrations/elevenlabs/config', { method:'PATCH', body:{ phoneId } });
    await load(); toast('Phone saved');
  };

  const setVoice = async (voiceId) => {
    await api('/api/integrations/elevenlabs/config', { method:'PATCH', body:{ voiceId } });
    await load(); toast('Voice saved');
  };

  if (!s) return null;
  if (!s.keyValid) {
    return <div style={{ ...styles.card, padding:24, marginBottom:24, borderColor:G.red+'66' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontSize:24 }}>🎙️</span>
        <h2 style={{ margin:0, fontSize:20 }}>ElevenLabs Voice AI</h2>
        <Badge c={G.red} bg={G.red+'22'}>API key missing or invalid</Badge>
      </div>
      <div style={{ color:G.textMuted, fontSize:13, marginTop:8 }}>{s.error || 'Set ELEVENLABS_API_KEY on the platform to enable voice agents.'}</div>
    </div>;
  }

  const state = s.configured ? 'live' : 'needs_phone';
  const c = state === 'live' ? G.green : G.yellow;

  return <div style={{ ...styles.card, padding:24, marginBottom:24, borderColor: c+'66' }}>
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:14, flexWrap:'wrap' }}>
      <div style={{ flex:1, minWidth:280 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
          <span style={{ fontSize:24 }}>🎙️</span>
          <h2 style={{ margin:0, fontSize:20 }}>ElevenLabs Voice AI</h2>
          <Badge c={c} bg={c+'22'}>
            <Dot c={c} pulse={state==='needs_phone'}/>{' '}
            {state === 'live' ? 'Live · AI voice agent ready' : 'Almost there — import your phone number'}
          </Badge>
        </div>
        <div style={{ color:G.textMuted, fontSize:14, lineHeight:1.6 }}>
          {state === 'live'
            ? 'AI voice agent will answer inbound calls + dial leads to qualify them. Calls captured to the contact timeline automatically.'
            : 'Your Twilio number needs to be imported into ElevenLabs before they can place calls. One click does it.'}
        </div>
        {s.characterCount !== undefined && <div style={{ fontSize:12, color:G.textDim, marginTop:8 }}>
          Plan: <strong>{s.subscription || 'Free'}</strong> · Usage this month: {Math.round(s.characterCount/1000)}k / {Math.round((s.characterLimit||0)/1000)}k characters
        </div>}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {state === 'needs_phone' && <Btn v="primary" onClick={importNumber} disabled={busy}>{busy ? <Spinner/> : 'Import Twilio Number'}</Btn>}
        {state === 'live' && <Btn v="ghost" sz="sm" onClick={()=>{ setShowAdvanced(!showAdvanced); if (!showAdvanced) loadDetails(); }}>{showAdvanced ? 'Hide' : 'Configure'}</Btn>}
      </div>
    </div>

    {showAdvanced && state === 'live' && <>
      <div style={{ marginTop:16, paddingTop:16, borderTop:`1px solid ${G.border}`, display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <Field label="Outbound phone (ElevenLabs side)">
          {phones.length === 0 ? (
            <div style={{ padding:'10px 12px', background:G.yellow+'18', border:`1px solid ${G.yellow}55`, borderRadius:8, fontSize:13, color:G.text }}>
              No numbers imported into ElevenLabs yet. Click below to import this account's Twilio number now.
              <div style={{ marginTop:8 }}>
                <Btn v="primary" sz="sm" onClick={importNumber} disabled={busy}>{busy ? <Spinner/> : 'Import Twilio Number'}</Btn>
              </div>
            </div>
          ) : (
            <>
              <Select value={s.phoneId||''} onChange={e=>setPhone(e.target.value)}>
                {!s.phoneId && <option value="">Select a number…</option>}
                {phones.map(p => <option key={p.id} value={p.id}>{p.number} {p.label?`(${p.label})`:''}</option>)}
              </Select>
              {s.phoneId && !phones.find(p => p.id === s.phoneId) && (
                <div style={{ marginTop:6, fontSize:12, color:G.yellow }}>
                  Saved phone ID isn't in your ElevenLabs account anymore. <button onClick={importNumber} disabled={busy} style={{ background:'none', border:'none', color:G.purple, textDecoration:'underline', cursor:'pointer', padding:0 }}>Re-import the Twilio number</button>.
                </div>
              )}
              <div style={{ marginTop:6 }}>
                <Btn v="ghost" sz="xs" onClick={importNumber} disabled={busy}>{busy ? '…' : '+ Import a new Twilio number'}</Btn>
              </div>
            </>
          )}
        </Field>
        <Field label="Voice">
          <Select value={s.voiceId||''} onChange={e=>setVoice(e.target.value)}>
            {voices.slice(0,30).map(v => <option key={v.id} value={v.id}>{v.name} ({v.category||'standard'})</option>)}
          </Select>
        </Field>
      </div>
      <CloneVoiceWidget toast={toast} onCloned={async (vid) => { await api('/api/integrations/elevenlabs/config', { method:'PATCH', body:{ voiceId: vid } }); load(); loadDetails(); toast('Voice cloned and set as default'); }}/>
      <AgentOnboardingForm toast={toast}/>
    </>}
  </div>;
}

// Per-account values that fill in the booking-assistant system prompt.
// Saving auto-resets provisioned agents so the next call uses the new prompt.
function AgentOnboardingForm({ toast }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [repairing, setRepairing] = useState(false);

  const load = () => api('/api/integrations/elevenlabs/agent-config').then(setCfg).catch(()=>{});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api('/api/integrations/elevenlabs/agent-config', { method:'PATCH', body: {
        inspectionFee: cfg.inspectionFee, serviceAreaText: cfg.serviceAreaText, membershipLink: cfg.membershipLink,
      }});
      toast('Saved. Agents will pick up the new prompt on the next call.', 'success');
    } catch (e) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const repairInbound = async () => {
    setRepairing(true);
    try {
      const r = await api('/api/integrations/elevenlabs/repair-inbound', { method:'POST' });
      toast(`Inbound agent re-provisioned (agent_id: ${r.agentId?.slice(0, 14)}…)`, 'success');
    } catch (e) { toast(e.message, 'error'); }
    finally { setRepairing(false); }
  };

  if (!cfg) return null;
  return <div style={{ marginTop:18, paddingTop:18, borderTop:`1px solid ${G.border}` }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
      <div>
        <div style={{ fontSize:15, fontWeight:700 }}>Booking-assistant setup</div>
        <div style={{ fontSize:12, color:G.textMuted }}>These fill in the AI agent's script. Save to push the change to your phone agents on the next call.</div>
      </div>
      <Btn v="ghost" sz="sm" onClick={repairInbound} disabled={repairing} title="Re-create the inbound agent with the latest prompt and re-assign it to your imported number.">
        {repairing ? <Spinner/> : 'Repair inbound'}
      </Btn>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:12 }}>
      <Field label="Business name (read-only)">
        <Input value={cfg.businessName || ''} disabled/>
      </Field>
      <Field label="Phone (read-only)">
        <Input value={cfg.phone || ''} disabled/>
      </Field>
      <Field label="Booking link (read-only)">
        <Input value={cfg.bookingLink || ''} disabled/>
      </Field>
      <Field label="Inspection fee" hint="Default 69. Use 'free' if you don't charge one.">
        <Input value={cfg.inspectionFee || ''} onChange={e=>setCfg({...cfg, inspectionFee: e.target.value})} placeholder="69"/>
      </Field>
      <Field label="Service area" hint="Zip codes or regions you cover.">
        <Input value={cfg.serviceAreaText || ''} onChange={e=>setCfg({...cfg, serviceAreaText: e.target.value})} placeholder="33602, 33603, 33604…"/>
      </Field>
      <Field label="Membership plan link" hint="Optional. Used after a booking.">
        <Input value={cfg.membershipLink || ''} onChange={e=>setCfg({...cfg, membershipLink: e.target.value})} placeholder="https://…"/>
      </Field>
    </div>
    <div style={{ marginTop:12, textAlign:'right' }}>
      <Btn v="primary" onClick={save} disabled={saving}>{saving ? <Spinner/> : 'Save + reset agents'}</Btn>
    </div>
  </div>;
}

function StripeWebhookCard({ toast, user }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Super-admin home view only — hide while ghosting into a sub-account
  if (user?.role !== 'super_admin' || user?.accountId) return null;
  const run = async () => {
    setBusy(true);
    try {
      const r = await api('/api/admin-panel/stripe-create-webhook', { method:'POST' });
      setResult(r);
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <div style={{ ...styles.card, padding:18, marginBottom:18, borderColor:G.purple+'66' }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
      <span style={{ fontSize:22 }}>🪝</span>
      <h3 style={{ margin:0 }}>Stripe Webhook (super-admin)</h3>
    </div>
    <div style={{ fontSize:13, color:G.textMuted, lineHeight:1.55, marginBottom:14 }}>
      Creates the live Stripe webhook endpoint so subscription + Connect events sync back into the app (renewals, cancels, past-due flags).
      One-click setup — uses the live STRIPE_SECRET_KEY already on the platform.
    </div>
    {!result && <Btn v="primary" onClick={run} disabled={busy}>{busy ? <Spinner sz={14}/> : 'Create Stripe Webhook'}</Btn>}
    {result?.alreadyExists && <div style={{ ...styles.card, padding:12, background:G.yellowLight, fontSize:13 }}>
      ✓ Webhook already exists for {result.url}. Signing secret can only be viewed in Stripe Dashboard now (Stripe doesn't let APIs read it back).
      Go to https://dashboard.stripe.com/webhooks → click your endpoint → "Signing secret" reveal → copy → run
      <code style={{ display:'block', padding:8, marginTop:6, background:G.input, borderRadius:6, fontSize:11 }}>flyctl secrets set STRIPE_WEBHOOK_SECRET=whsec_... -a tradeops</code>
    </div>}
    {result?.signingSecret && <div style={{ ...styles.card, padding:14, marginTop:10, background:G.greenLight, borderColor:G.green+'66' }}>
      <div style={{ fontWeight:700, color:G.green, marginBottom:6 }}>✓ Created — copy signing secret below</div>
      <div style={{ fontSize:11, color:G.textMuted, marginBottom:8 }}>Run this command in your terminal to save it (only shown once):</div>
      <code style={{ display:'block', padding:10, background:G.input, borderRadius:6, fontSize:11, wordBreak:'break-all' }}>
        flyctl secrets set STRIPE_WEBHOOK_SECRET={result.signingSecret} -a tradeops
      </code>
      <Btn sz="sm" onClick={()=>{ navigator.clipboard.writeText(result.signingSecret); toast('Secret copied'); }} style={{ marginTop:10 }}>Copy secret</Btn>
    </div>}
  </div>;
}

// R&R backlink automation — Bing Webmaster + Dev.to + Hashnode + Blogger
// (all API-key or blog-ID pastes; no OAuth screens beyond what Google already
// has). Every configured platform = one real backlink per blog post per site.
function RankAndRentApiKeysCard({ toast }) {
  const [status, setStatus] = useState(null);
  const [keys, setKeys] = useState({ devto:'', hashnode:'', hashnode_publication:'', blogger:'', bing:'' });
  const [saving, setSaving] = useState(null);

  const load = () => Promise.all([
    api('/api/sites/rank-and-rent/syndication/status').catch(()=>null),
  ]).then(([s]) => setStatus(s));
  useEffect(() => { load(); }, []);

  const save = async (provider) => {
    const value = keys[provider];
    if (!value?.trim()) return;
    setSaving(provider);
    try {
      if (provider === 'bing') {
        await api('/api/sites/rank-and-rent/bing/connect', { method:'POST', body:{ apiKey: value.trim() }});
      } else {
        await api('/api/sites/rank-and-rent/syndication/connect', { method:'POST', body:{ provider, value: value.trim() }});
      }
      toast('✓ Saved');
      setKeys({ ...keys, [provider]: '' });
      load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setSaving(null); }
  };

  const rows = [
    { key:'bing',                  label:'Bing Webmaster',        placeholder:'Bing API key', help:'bing.com/webmasters → Settings → API Access', ok: false },
    { key:'devto',                 label:'Dev.to',                placeholder:'Dev.to API key', help:'dev.to/settings/extensions → Generate API Key', ok: status?.status?.devto },
    { key:'hashnode',              label:'Hashnode API key',      placeholder:'Hashnode PAT', help:'hashnode.com/settings/developer → Personal Access Token', ok: status?.status?.hashnode },
    { key:'hashnode_publication',  label:'Hashnode publication ID', placeholder:'6xxxxxxx…', help:'Find in your Hashnode blog settings URL: /publications/<ID>', ok: status?.status?.hashnode },
    { key:'blogger',               label:'Blogger blog ID',       placeholder:'12345678901234567890', help:'Blogger settings URL: blogger.com/blog/posts/<ID>', ok: status?.status?.blogger },
  ];

  return <div style={{ ...styles.card, marginBottom:20 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
      <h3 style={{ margin:0 }}>🔗 Rank-and-Rent Backlink Automation</h3>
      {status?.stats?.total > 0 && <Badge c={G.green} bg={G.greenLight}>{status.stats.total} backlinks / 30d</Badge>}
    </div>
    <div style={{ fontSize:12, color:G.textMuted, marginBottom:14, lineHeight:1.5 }}>
      Paste API keys once → every AI-generated blog post gets auto-published to Dev.to + Hashnode + Blogger with a canonical link back to your R&R site. Each successful publish = 1 real backlink from a high-authority domain. Bing Webmaster surfaces backlink counts + Bing/DuckDuckGo/Perplexity search stats.
    </div>
    {rows.map(r => (
      <div key={r.key} style={{ marginBottom:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
          <strong style={{ fontSize:13 }}>{r.label}</strong>
          {r.ok && <Badge c={G.green} bg={G.greenLight}>Connected</Badge>}
        </div>
        <div style={{ display:'flex', gap:6 }}>
          <input value={keys[r.key]} onChange={e=>setKeys({ ...keys, [r.key]: e.target.value })}
            placeholder={r.placeholder}
            style={{ ...styles.inp, flex:1 }}/>
          <Btn sz="sm" v="primary" onClick={()=>save(r.key)} disabled={saving===r.key}>{saving===r.key?'…':'Save'}</Btn>
        </div>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:3 }}>{r.help}</div>
      </div>
    ))}
    {status?.stats?.byPlatform?.length > 0 && <div style={{ marginTop:14, padding:12, background:G.input, borderRadius:8 }}>
      <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', fontWeight:700, marginBottom:6 }}>Backlinks generated (last 30d)</div>
      <div style={{ display:'flex', gap:12 }}>
        {status.stats.byPlatform.map(p => <div key={p.platform} style={{ flex:1, textAlign:'center' }}>
          <div style={{ fontSize:18, fontWeight:800, color:G.green }}>{p.n}</div>
          <div style={{ fontSize:11, color:G.textMuted, textTransform:'capitalize' }}>{p.platform}</div>
        </div>)}
      </div>
    </div>}
  </div>;
}

function FinancingPartnersCard({ toast }) {
  const [urls, setUrls] = useState(null);
  useEffect(() => { api('/api/portal/financing/urls').then(setUrls).catch(()=>{}); }, []);
  if (!urls) return null;
  const save = async (k, v) => {
    setUrls({ ...urls, [k]: v });
    try { await api('/api/portal/financing/urls', { method:'PATCH', body:{ [k]: v }}); }
    catch (e) { toast(e.message, 'error'); }
  };
  const setDefault = async (k) => {
    setUrls({ ...urls, defaultPartner: k });
    try { await api('/api/portal/financing/urls', { method:'PATCH', body:{ defaultPartner: k }}); toast('Default partner saved'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const partners = [
    { key:'wisetack', label:'Wisetack', desc:'Best for $500-$25k tickets · 30s approval, no hard pull · home services native', signupUrl:'https://wisetack.com' },
    { key:'sunbit', label:'Sunbit', desc:'$200-$10k · 90%+ approval rate · soft credit pull · best for smaller-ticket repairs', signupUrl:'https://sunbit.com' },
    { key:'affirm', label:'Affirm Business', desc:'$50-$30k · BNPL leader · longer terms for big-ticket jobs', signupUrl:'https://www.affirm.com/business' },
    { key:'service_finance', label:'Service Finance Company', desc:'HVAC + home reno specialist · up to 12-year terms · Truist-backed', signupUrl:'https://servicefinanceco.com' },
    { key:'synchrony', label:'Synchrony Home', desc:'Brand-tied (Carrier, Lennox, Trane) · authorized dealer required', signupUrl:'https://www.synchrony.com' },
    { key:'enerbank', label:'EnerBank USA', desc:'HVAC + solar focus · $1k-$75k', signupUrl:'https://www.enerbank.com' },
    { key:'greensky', label:'GreenSky', desc:'Owned by Goldman Sachs · home improvement specialist · 2+ years in business required', signupUrl:'https://www.greenskycredit.com' },
  ];
  return <div style={{ ...styles.card, padding:18, marginBottom:18, borderColor:G.green+'66' }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
      <span style={{ fontSize:22 }}>💳</span>
      <h3 style={{ margin:0 }}>Financing Partners</h3>
    </div>
    <div style={{ fontSize:13, color:G.textMuted, lineHeight:1.55, marginBottom:14 }}>
      Sign up directly with any partner below (it's free — they pay you a referral fee per funded loan). Then paste your unique merchant link here. When a customer clicks "Apply for Financing" on an estimate, we redirect them to your partner's application page.
    </div>
    {partners.map(p => (
      <div key={p.key} style={{ ...styles.card, padding:12, marginBottom:8, background:G.input }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6, gap:10 }}>
          <div>
            <strong>{p.label}</strong>
            {urls.defaultPartner === p.key && <Badge c={G.green} bg={G.greenLight} style={{ marginLeft:8 }}>DEFAULT</Badge>}
            <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{p.desc}</div>
          </div>
          <div style={{ display:'flex', gap:6 }}>
            <Btn sz="xs" v="ghost" onClick={()=>window.open(p.signupUrl,'_blank')}>Sign up ↗</Btn>
            {urls.defaultPartner !== p.key && urls[p.key] && <Btn sz="xs" onClick={()=>setDefault(p.key)}>Set default</Btn>}
          </div>
        </div>
        <Input
          placeholder={`Paste your ${p.label} merchant URL here...`}
          value={urls[p.key] || ''}
          onChange={e => setUrls({ ...urls, [p.key]: e.target.value })}
          onBlur={e => save(p.key, e.target.value)}
          style={{ fontSize:12, fontFamily:'monospace' }}
        />
      </div>
    ))}
    <div style={{ fontSize:11, color:G.textDim, marginTop:8, lineHeight:1.5 }}>
      💡 Tip: sign up with 2-3 partners. The default partner shows first on estimates; others appear as backup options. Customers see the recommended partner highlighted based on their loan amount.
    </div>
  </div>;
}

function BookingWidgetCard({ toast }) {
  const [cfg, setCfg] = useState(null);
  const [copied, setCopied] = useState(null);
  useEffect(() => { api('/api/booking-widget/_config/me').then(setCfg).catch(()=>{}); }, []);
  if (!cfg) return null;
  const save = async (patch) => {
    try { await api('/api/booking-widget/_config/me', { method:'PATCH', body: patch }); toast('Saved'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const copy = (text, label) => { navigator.clipboard.writeText(text); setCopied(label); setTimeout(()=>setCopied(null),2000); };
  return <div style={{ ...styles.card, padding:18, marginBottom:18, borderColor:G.purple+'66' }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
      <span style={{ fontSize:22 }}>📅</span>
      <h3 style={{ margin:0 }}>Website Booking Widget</h3>
      <Badge c={cfg.enabled?G.green:G.textMuted} bg={(cfg.enabled?G.green:G.textMuted)+'22'}>{cfg.enabled?'ENABLED':'DISABLED'}</Badge>
    </div>
    <div style={{ fontSize:13, color:G.textMuted, lineHeight:1.55, marginBottom:12 }}>
      Drop one line on your existing website and customers can book directly. Submissions create a contact + job in TradeOps automatically.
    </div>
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:4 }}>Public URL</div>
      <div style={{ display:'flex', gap:8 }}>
        <Input value={cfg.publicUrl} readOnly style={{ fontFamily:'monospace', fontSize:12 }}/>
        <Btn sz="sm" onClick={()=>copy(cfg.publicUrl,'url')}>{copied==='url'?'✓ Copied':'Copy'}</Btn>
      </div>
    </div>
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:4 }}>Embed script (1-line)</div>
      <div style={{ display:'flex', gap:8 }}>
        <Input value={cfg.scriptSnippet} readOnly style={{ fontFamily:'monospace', fontSize:11 }}/>
        <Btn sz="sm" onClick={()=>copy(cfg.scriptSnippet,'script')}>{copied==='script'?'✓ Copied':'Copy'}</Btn>
      </div>
      <div style={{ fontSize:11, color:G.textDim, marginTop:4 }}>Paste before <code style={{ color:G.cyan }}>&lt;/body&gt;</code> on your site. Floating "📅 Book Online" button appears bottom-right.</div>
    </div>
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:4 }}>iframe (inline)</div>
      <div style={{ display:'flex', gap:8 }}>
        <Input value={cfg.embedSnippet} readOnly style={{ fontFamily:'monospace', fontSize:11 }}/>
        <Btn sz="sm" onClick={()=>copy(cfg.embedSnippet,'iframe')}>{copied==='iframe'?'✓ Copied':'Copy'}</Btn>
      </div>
    </div>
    <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:G.textMuted, cursor:'pointer' }}>
      <input type="checkbox" checked={cfg.enabled} onChange={e=>save({ enabled: e.target.checked })}/>
      <span>Booking widget enabled</span>
    </label>
  </div>;
}

// ─── Calendar Connectors (Google + Outlook) ────────────────────────────────
function CalendarConnectorsCard({ toast, status, refreshStatus }) {
  const [diag, setDiag] = useState({});      // { gcal: {...}, outlook: {...} }
  const [diagBusy, setDiagBusy] = useState(null);
  const connect = async (provider) => {
    try {
      const { url } = await api(`/api/integrations/${provider}/connect`);
      window.location.href = url;
    } catch(e) { toast(e.message,'error'); }
  };
  const disconnect = async (provider) => {
    if (!confirm(`Disconnect ${provider === 'gcal' ? 'Google Calendar' : 'Outlook'}?`)) return;
    try { await api(`/api/integrations/${provider}/disconnect`, { method:'POST' }); refreshStatus?.(); toast('Disconnected'); }
    catch(e) { toast(e.message,'error'); }
  };
  const diagnose = async (provider) => {
    setDiagBusy(provider);
    try {
      const result = await api(`/api/integrations/${provider}/diagnose`);
      setDiag(d => ({ ...d, [provider]: result }));
      const okIn  = result?.inbound?.ok;
      const okOut = result?.outbound?.ok;
      if (okIn && okOut) toast('Sync OK both directions');
      else if (okIn || okOut) toast('Partial sync — see details', 'error');
      else toast('Sync failed both directions — see details', 'error');
    } catch(e) {
      setDiag(d => ({ ...d, [provider]: { error: e.message } }));
      toast(e.message, 'error');
    } finally { setDiagBusy(null); }
  };
  const cal = (provider, label, icon, color, info) => {
    const isConnected = !!info?.connected;
    const d = diag[provider];
    const lastSync   = info?.lastSyncedAt;
    const lastError  = info?.lastError;
    return (
      <div key={provider} style={{ ...styles.card, padding:14, borderColor: isConnected ? color+'66' : G.cardBorder }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:42, height:42, borderRadius:10, background:color+'22', color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>{icon}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <strong style={{ fontSize:14 }}>{label}</strong>
              {isConnected ? <Badge c={G.green} bg={G.greenLight}>Connected</Badge> : <Badge c={G.textMuted} bg={G.input}>Not connected</Badge>}
            </div>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>
              {isConnected ? (info.email || 'Two-way sync active') : 'Two-way sync. Jobs push to your calendar; busy times block scheduling.'}
            </div>
            {isConnected && lastSync && (
              <div style={{ fontSize:11, color:G.textDim, marginTop:2 }}>
                Last successful sync: {new Date(lastSync).toLocaleString()}
              </div>
            )}
            {isConnected && lastError && (
              <div style={{ fontSize:11, color:G.red, marginTop:2 }}>
                ⚠️ Last error: {lastError}
              </div>
            )}
          </div>
          {!isConnected
            ? <Btn sz="sm" v="primary" onClick={()=>connect(provider)}>Connect</Btn>
            : <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                <Btn sz="sm" v="ghost" onClick={()=>diagnose(provider)} disabled={diagBusy===provider}>
                  {diagBusy===provider ? '…' : '🔍 Test Sync'}
                </Btn>
                <Btn sz="sm" v="danger" onClick={()=>disconnect(provider)}>Disconnect</Btn>
              </div>}
        </div>
        {d && (
          <div style={{ marginTop:10, padding:10, background:G.input, borderRadius:8, fontSize:12 }}>
            {d.error && <div style={{ color:G.red }}>⚠️ {d.error}</div>}
            {d.inbound && (
              <div style={{ marginTop:4 }}>
                <span style={{ fontWeight:600 }}>Inbound (calendar → TradeOps):</span>{' '}
                {d.inbound.ok
                  ? <span style={{ color:G.green }}>✓ Read {d.inbound.eventCount ?? 0} events</span>
                  : <span style={{ color:G.red }}>✗ {d.inbound.error || 'failed'}</span>}
              </div>
            )}
            {d.outbound && (
              <div style={{ marginTop:4 }}>
                <span style={{ fontWeight:600 }}>Outbound (TradeOps → calendar):</span>{' '}
                {d.outbound.ok
                  ? <span style={{ color:G.green }}>✓ Created + deleted test event{d.outbound.eventId ? ` (id: ${String(d.outbound.eventId).slice(0,12)}…)` : ''}</span>
                  : <span style={{ color:G.red }}>✗ {d.outbound.error || 'failed'}</span>}
              </div>
            )}
            {(d.outbound?.ok || d.inbound?.ok) && <div style={{ marginTop:6, fontSize:11, color:G.textDim }}>If both rows are green but real jobs still don't appear, the assigned tech may not have their calendar connected, or the job has no scheduledDate/startTime set.</div>}
          </div>
        )}
      </div>
    );
  };
  return <div style={{ ...styles.card, padding:18, marginBottom:14 }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
      <span style={{ fontSize:22 }}>🗓️</span>
      <h3 style={{ margin:0 }}>Calendar Sync</h3>
    </div>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>
      Two-way sync with your personal calendar. Jobs created in TradeOps appear on your calendar; meetings + busy times on your calendar block customers from booking conflicting times.
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:10 }}>
      {cal('gcal',    'Google Calendar', '🗓️', '#4285f4', status?.gcal)}
      {cal('outlook', 'Outlook Calendar', '📆', '#0078d4', status?.outlook)}
    </div>
  </div>;
}

// ─── Lead Capture (universal webhook + inbound email + FB Lead Ads) ────────
function LeadCaptureCard({ toast }) {
  const [cfg, setCfg] = useState(null);
  const [recent, setRecent] = useState([]);
  const [diag, setDiag] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => {
    api('/api/lead-capture/config').then(setCfg).catch(()=>{});
    api('/api/lead-capture/recent').then(r=>setRecent(r.leads||[])).catch(()=>{});
  };
  useEffect(() => { load(); }, []);
  const copy = (t) => { try { navigator.clipboard.writeText(t); toast('Copied'); } catch {} };
  const rotate = async () => {
    if (!confirm('Rotate the lead webhook URL? Any Zaps/integrations using the old URL will stop working.')) return;
    try { await api('/api/lead-capture/rotate-token', { method:'POST' }); load(); toast('New webhook URL generated'); }
    catch(e) { toast(e.message,'error'); }
  };
  const runDiagnose = async () => {
    setBusy('diagnose');
    try { setDiag(await api('/api/lead-capture/diagnose')); }
    catch(e) { toast(e.message, 'error'); setDiag({ error: e.message }); }
    finally { setBusy(null); }
  };
  const runTest = async () => {
    setBusy('test');
    try {
      const r = await api('/api/lead-capture/test', { method:'POST', body: {} });
      if (r.contactId) {
        toast(`✓ Test contact created (${r.isNew ? 'new' : 'matched existing'})`);
        load();
      } else {
        toast(`Test failed: ${r.error || 'unknown'}`, 'error');
      }
      setDiag(prev => ({ ...(prev||{}), lastTest: r }));
    } catch(e) {
      toast(`Test failed: ${e.message}`, 'error');
      setDiag(prev => ({ ...(prev||{}), lastTest: { error: e.message } }));
    } finally { setBusy(null); }
  };
  if (!cfg) return null;
  return <div style={{ ...styles.card, padding:18, marginBottom:14 }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
      <span style={{ fontSize:22 }}>📥</span>
      <h3 style={{ margin:0 }}>Lead Capture</h3>
      <Badge c={G.purple} bg={G.purpleDim}>3 channels</Badge>
    </div>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>
      Connect any marketing system (your website, Angi, HomeAdvisor, Thumbtack, Facebook Lead Ads, Zapier, Make.com) — leads land as Contacts + fire AI SMS within seconds.
    </div>

    {/* 1. Webhook URL */}
    <div style={{ padding:12, background:G.input, borderRadius:8, marginBottom:10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <strong style={{ fontSize:13 }}>🔗 Universal Webhook URL</strong>
        <div style={{ display:'flex', gap:6 }}>
          <Btn sz="xs" v="primary" onClick={runTest} disabled={busy==='test'}>{busy==='test'?'…':'🧪 Send Test Lead'}</Btn>
          <Btn sz="xs" v="ghost" onClick={runDiagnose} disabled={busy==='diagnose'}>{busy==='diagnose'?'…':'🔍 Diagnose'}</Btn>
          <Btn sz="xs" v="ghost" onClick={rotate}>🔄 Rotate</Btn>
        </div>
      </div>
      <div style={{ display:'flex', gap:6 }}>
        <code style={{ flex:1, padding:'8px 10px', background:G.bg, borderRadius:6, fontSize:11, wordBreak:'break-all' }}>{cfg.webhookUrl}</code>
        <Btn sz="xs" onClick={()=>copy(cfg.webhookUrl)}>📋</Btn>
      </div>
      <div style={{ fontSize:11, color:G.textMuted, marginTop:6, lineHeight:1.5 }}>
        Paste into Zapier/Make.com as a Webhooks → POST step. Send JSON with <code>name</code>, <code>phone</code>, optional <code>email</code>, <code>notes</code>, <code>service</code>, <code>address</code>, <code>source</code>.
      </div>
      {diag && (
        <div style={{ marginTop:10, padding:10, background:G.bg, borderRadius:6, fontSize:12, border:`1px solid ${G.cardBorder}` }}>
          {diag.error && <div style={{ color:G.red }}>⚠️ {diag.error}</div>}
          {diag.hasToken !== undefined && (
            <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap:'4px 12px' }}>
              <span style={{ color:G.textMuted }}>Token issued:</span><span style={{ color: diag.hasToken ? G.green : G.red }}>{diag.hasToken ? '✓ yes' : '✗ no — open this card once to generate'}</span>
              <span style={{ color:G.textMuted }}>Subscription:</span><span>{diag.subscriptionStatus}</span>
              <span style={{ color:G.textMuted }}>Contacts total:</span><span>{diag.contactsTotal}</span>
              <span style={{ color:G.textMuted }}>From webhook sources:</span><span>{diag.contactsFromWebhookSources}</span>
              <span style={{ color:G.textMuted }}>Recent payloads:</span><span>{diag.recent?.length || 0}</span>
              <span style={{ color:G.textMuted }}>Processed:</span><span style={{ color: G.green }}>{diag.recent?.filter(r=>r.processed).length || 0}</span>
              <span style={{ color:G.textMuted }}>With errors:</span><span style={{ color: G.red }}>{diag.recent?.filter(r=>r.error).length || 0}</span>
            </div>
          )}
          {diag.hint && <div style={{ marginTop:6, color:G.textMuted, fontStyle:'italic' }}>{diag.hint}</div>}
          {diag.lastTest && (
            <div style={{ marginTop:8, padding:8, background:diag.lastTest.error ? G.red+'18' : G.green+'18', borderRadius:6 }}>
              <strong>Last test:</strong> {diag.lastTest.error
                ? <span style={{ color:G.red }}>✗ {diag.lastTest.error}</span>
                : <span style={{ color:G.green }}>✓ Created contact {String(diag.lastTest.contactId||'').slice(0,12)}…</span>}
            </div>
          )}
        </div>
      )}
    </div>

    {/* 2. Inbound email */}
    <div style={{ padding:12, background:G.input, borderRadius:8, marginBottom:10 }}>
      <strong style={{ fontSize:13 }}>📧 Forward leads to this email</strong>
      <div style={{ display:'flex', gap:6, marginTop:6 }}>
        <code style={{ flex:1, padding:'8px 10px', background:G.bg, borderRadius:6, fontSize:11 }}>{cfg.inboxEmail}</code>
        <Btn sz="xs" onClick={()=>copy(cfg.inboxEmail)}>📋</Btn>
      </div>
      <div style={{ fontSize:11, color:G.textMuted, marginTop:6, lineHeight:1.5 }}>
        Set up your Angi / HomeAdvisor / Thumbtack / Porch / Bark email notifications to forward to this address. We auto-parse the lead.
      </div>
    </div>

    {/* 3. Facebook Lead Ads */}
    <div style={{ padding:12, background:G.input, borderRadius:8, marginBottom:10 }}>
      <strong style={{ fontSize:13 }}>📘 Facebook Lead Ads</strong>
      <div style={{ fontSize:11, color:G.textMuted, marginTop:6, lineHeight:1.5 }}>
        Once you connect Facebook (Integrations → Social Posting → Facebook), lead-form submissions auto-flow in.
        No extra setup needed.
      </div>
    </div>

    {/* Recent leads */}
    {recent.length > 0 && (
      <div style={{ marginTop:14 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <strong style={{ fontSize:12, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Recent intake</strong>
          <Btn sz="xs" v="ghost" onClick={load}>↻ Refresh</Btn>
        </div>
        <div style={{ marginTop:8, maxHeight:240, overflowY:'auto' }}>
          {recent.map(r => (
            <div key={r.id} style={{ padding:'8px 0', fontSize:12, borderBottom:`1px solid ${G.cardBorder}55` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                <span style={{ flex:1 }}>
                  {r.first_name || (r.raw_payload?.name || r.raw_payload?.full_name || '(name missing)')} {r.last_name||''}
                  {(r.phone || r.email) && <span style={{ color:G.textMuted }}> · {[r.phone, r.email].filter(Boolean).join(' / ')}</span>}
                  <span style={{ color:G.textMuted }}> · {r.source}</span>
                </span>
                <span style={{ color: r.error ? G.red : r.processed ? G.green : G.yellow, fontWeight:600, whiteSpace:'nowrap' }}>
                  {r.error ? '⚠️' : r.processed ? '✓' : '○'} {new Date(r.received_at).toLocaleString()}
                </span>
              </div>
              {r.error && (
                <div style={{ marginTop:4, padding:'4px 8px', background:G.red+'18', border:`1px solid ${G.red}55`, borderRadius:4, color:G.red, fontSize:11, display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                  <span>⚠️ {r.error}</span>
                  <Btn sz="xs" v="ghost" onClick={async ()=>{
                    try { await api(`/api/lead-capture/retry/${r.id}`, { method:'POST' }); toast('✓ Retried — contact created'); load(); }
                    catch(e) { toast(`Retry failed: ${e.message}`, 'error'); }
                  }}>↻ Retry</Btn>
                </div>
              )}
              {!r.processed && !r.error && r.raw_payload && (
                <div style={{ marginTop:4, padding:'4px 8px', background:G.yellow+'18', border:`1px solid ${G.yellow}55`, borderRadius:4, color:G.yellow, fontSize:11 }}>
                  ⏳ Webhook landed but contact not yet created. <Btn sz="xs" v="ghost" onClick={async ()=>{
                    try { await api(`/api/lead-capture/retry/${r.id}`, { method:'POST' }); toast('✓ Processed'); load(); }
                    catch(e) { toast(`Process failed: ${e.message}`, 'error'); }
                  }}>Process now</Btn>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )}
  </div>;
}

// ─── Social Posting (Facebook, Instagram, YouTube, TikTok, GBP) ────────────
const SOCIAL_PLATFORMS = [
  { key:'facebook_page',     label:'Facebook + Instagram',    icon:'📘', color:'#1877f2', subnote: 'IG Business auto-attaches when linked to your Page' },
  { key:'youtube',           label:'YouTube',                 icon:'▶️', color:'#ff0000' },
  { key:'tiktok_organic',    label:'TikTok',                  icon:'🎵', color:'#000' },
  { key:'gbp',               label:'Google Business Profile', icon:'📍', color:'#4285f4' },
];

function SocialPostingCard({ toast }) {
  const [conns, setConns] = useState({});
  const [picker, setPicker] = useState(null);
  const load = () => api('/api/integrations/connections').then(setConns).catch(()=>{});
  useEffect(() => { load(); }, []);

  const connect = async (provider) => {
    try {
      const { url } = await api(`/api/integrations/${provider}/connect`);
      window.location.href = url;
    } catch(e) { toast(e.message,'error'); }
  };
  const disconnect = async (provider) => {
    if (!confirm(`Disconnect ${provider}?`)) return;
    try { await api(`/api/integrations/${provider}/disconnect`, { method:'POST' }); load(); toast('Disconnected'); }
    catch(e) { toast(e.message,'error'); }
  };
  const pickFacebookPage = async () => {
    try {
      const r = await api('/api/social/facebook/pages');
      setPicker({ provider:'facebook_page', pages: r.pages });
    } catch(e) { toast(e.message,'error'); }
  };

  return <div style={{ ...styles.card, padding:18, marginBottom:14 }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
      <span style={{ fontSize:22 }}>📲</span>
      <h3 style={{ margin:0 }}>Social Posting</h3>
      <Badge c={G.purple} bg={G.purpleDim}>NEW</Badge>
    </div>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14, lineHeight:1.55 }}>
      Auto-publish AI-generated creatives + job photos + review requests to your social channels. One post, many channels.
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10 }}>
      {SOCIAL_PLATFORMS.map(p => {
        const c = conns[p.key] || {};
        const isConnected = !!c.connected;
        return (
          <div key={p.key} style={{ ...styles.card, padding:14, borderColor: isConnected ? p.color+'66' : G.cardBorder }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
              <div style={{ width:36, height:36, borderRadius:8, background:p.color+'22', color:p.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>{p.icon}</div>
              <div style={{ flex:1 }}>
                <strong style={{ fontSize:13 }}>{p.label}</strong>
                <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>
                  {isConnected ? '✓ Connected' : 'Not connected'}
                </div>
              </div>
            </div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {!isConnected && <Btn sz="xs" v="primary" onClick={()=>connect(p.key)}>Connect</Btn>}
              {isConnected && p.key==='facebook_page' && <Btn sz="xs" onClick={pickFacebookPage}>Pick Page</Btn>}
              {isConnected && <Btn sz="xs" v="danger" onClick={()=>disconnect(p.key)}>Disconnect</Btn>}
            </div>
          </div>
        );
      })}
    </div>

    {picker && <Modal open onClose={()=>setPicker(null)} width={520}>
      <h2 style={{ margin:'0 0 14px 0' }}>Pick your Facebook Page</h2>
      <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>
        We'll also link any connected Instagram Business account on the same page.
      </div>
      <div style={{ maxHeight:380, overflow:'auto' }}>
        {picker.pages.length === 0 ? (
          <div style={{ padding:18, color:G.textMuted, textAlign:'center' }}>No pages found. Make sure you own at least one Facebook Page.</div>
        ) : picker.pages.map(p => (
          <div key={p.id} onClick={async () => {
            try {
              await api('/api/social/facebook/select-page', { method:'POST', body:{ page: p }});
              toast(`✓ Saved: ${p.name}${p.instagram_business_account ? ` + Instagram` : ''}`);
              setPicker(null);
              load();
            } catch(e) { toast(e.message,'error'); }
          }} style={{ padding:12, borderBottom:`1px solid ${G.cardBorder}`, cursor:'pointer' }}>
            <strong>{p.name}</strong>
            <div style={{ fontSize:12, color:G.textMuted }}>
              {p.id}
              {p.instagram_business_account && ` · 📷 @${p.instagram_business_account.username||p.instagram_business_account.id}`}
            </div>
          </div>
        ))}
      </div>
    </Modal>}
  </div>;
}

function ClaudeConnectorCard({ toast }) {
  const [keys, setKeys] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState('Claude Desktop');
  const [justCreated, setJustCreated] = useState(null);
  const [openSetup, setOpenSetup] = useState(false);

  const load = () => api('/api/mcp/keys').then(r => setKeys(r.keys || [])).catch(()=>{});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const r = await api('/api/mcp/keys', { method:'POST', body:{ label: newLabel || 'Claude Connector' }});
      setJustCreated(r);
      setShowCreate(false);
      setNewLabel('Claude Desktop');
      load();
    } catch(e) { toast(e.message,'error'); }
  };
  const revoke = async (id) => {
    if (!confirm('Revoke this key? Any Claude session using it will stop working.')) return;
    try { await api(`/api/mcp/keys/${id}/revoke`, { method:'POST' }); load(); toast('Revoked'); }
    catch(e) { toast(e.message,'error'); }
  };
  const copy = (text) => { try { navigator.clipboard.writeText(text); toast('Copied'); } catch {} };

  const openapiUrl = `${window.location.origin}/api/mcp/openapi.json`;
  const mcpUrl     = `${window.location.origin}/api/mcp/jsonrpc`;

  return <div style={{ ...styles.card, padding:18, marginBottom:14, borderColor:G.purple+'66' }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
      <span style={{ fontSize:22 }}>🧠</span>
      <h3 style={{ margin:0 }}>Claude Connector</h3>
      <Badge c={G.purple} bg={G.purpleDim}>NEW</Badge>
    </div>
    <div style={{ fontSize:13, color:G.textMuted, lineHeight:1.55 }}>
      Use TradeOps from inside <strong>Claude.ai</strong> or <strong>Claude Desktop</strong>. Ask things like
      <em> "What's on the schedule tomorrow?"</em>, <em>"Text Sarah Thompson asking if Tuesday still works"</em>,
      or <em>"Have the AI call every overdue invoice over $500"</em> — Claude pulls + acts via TradeOps.
      The high-value moments (live AI transcripts, payment review, full estimates) link you back to the app.
    </div>

    {keys.length === 0 && !justCreated && (
      <div style={{ marginTop:12 }}>
        <Btn v="primary" onClick={()=>setShowCreate(true)}>+ Generate API Key</Btn>
      </div>
    )}

    {showCreate && (
      <div style={{ marginTop:12, padding:14, background:G.input, borderRadius:10 }}>
        <Field label="Key label (so you remember which device it's on)">
          <Input value={newLabel} onChange={e=>setNewLabel(e.target.value)} placeholder="Claude Desktop"/>
        </Field>
        <div style={{ display:'flex', gap:8, marginTop:10, justifyContent:'flex-end' }}>
          <Btn onClick={()=>setShowCreate(false)}>Cancel</Btn>
          <Btn v="primary" onClick={create}>Generate</Btn>
        </div>
      </div>
    )}

    {justCreated && (
      <div style={{ marginTop:14, padding:14, background:G.greenLight, border:`1px solid ${G.green+'66'}`, borderRadius:10 }}>
        <strong style={{ color:G.green }}>🔑 Save this key — shown only once</strong>
        <div style={{ display:'flex', gap:8, marginTop:8 }}>
          <code style={{ flex:1, padding:'8px 10px', background:G.bg, borderRadius:6, fontSize:12, overflow:'auto' }}>{justCreated.plaintext}</code>
          <Btn sz="sm" onClick={()=>copy(justCreated.plaintext)}>📋 Copy</Btn>
        </div>
        <div style={{ marginTop:10, fontSize:13 }}>
          <strong>Setup in Claude (recommended — native MCP):</strong>
          <ol style={{ paddingLeft:20, marginTop:6, lineHeight:1.7 }}>
            <li>Go to <a href="https://claude.ai/settings/connectors" target="_blank" rel="noreferrer" style={{ color:G.cyan }}>claude.ai/settings/connectors</a></li>
            <li>Click <strong>+ Add custom connector</strong></li>
            <li>MCP URL: <code style={{ background:G.bg, padding:'2px 6px', borderRadius:4 }}>{mcpUrl}</code> <Btn sz="xs" onClick={()=>copy(mcpUrl)}>Copy</Btn></li>
            <li>Auth → Bearer token → paste the key above</li>
            <li>Save → ask Claude <em>"What's on my TradeOps schedule today?"</em></li>
          </ol>
          <div style={{ marginTop:10, padding:10, background:G.bg, borderRadius:6, fontSize:12, color:G.textMuted }}>
            <strong>Fallback (OpenAPI):</strong> some older Claude clients prefer the OpenAPI flavor —
            URL: <code style={{ background:G.input, padding:'1px 5px', borderRadius:3 }}>{openapiUrl}</code> <Btn sz="xs" onClick={()=>copy(openapiUrl)}>Copy</Btn>
          </div>
        </div>
        <Btn sz="sm" v="ghost" onClick={()=>setJustCreated(null)} style={{ marginTop:10 }}>Done — hide this</Btn>
      </div>
    )}

    {keys.length > 0 && !justCreated && (
      <div style={{ marginTop:12 }}>
        <table style={{ width:'100%', fontSize:13 }}>
          <thead><tr><th style={styles.th}>Label</th><th style={styles.th}>Created</th><th style={styles.th}>Last used</th><th style={styles.th}></th></tr></thead>
          <tbody>{keys.map(k => (
            <tr key={k.id}>
              <td style={styles.td}>{k.label}{k.revoked_at && <Badge c={G.red} bg={G.red+'22'} style={{ marginLeft:6 }}>revoked</Badge>}</td>
              <td style={styles.td}>{new Date(k.created_at).toLocaleDateString()}</td>
              <td style={styles.td}>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : <span style={{ color:G.textMuted }}>never</span>}</td>
              <td style={styles.td}>{!k.revoked_at && <Btn sz="xs" v="danger" onClick={()=>revoke(k.id)}>Revoke</Btn>}</td>
            </tr>
          ))}</tbody>
        </table>
        <div style={{ marginTop:10, display:'flex', gap:8 }}>
          <Btn sz="sm" v="primary" onClick={()=>setShowCreate(true)}>+ New Key</Btn>
          <Btn sz="sm" onClick={()=>setOpenSetup(!openSetup)}>{openSetup?'Hide':'Show'} Setup Instructions</Btn>
        </div>
        {openSetup && (
          <div style={{ marginTop:10, padding:12, background:G.input, borderRadius:8, fontSize:13, lineHeight:1.7 }}>
            <strong>OpenAPI URL:</strong> <code style={{ background:G.bg, padding:'2px 6px', borderRadius:4 }}>{openapiUrl}</code> <Btn sz="xs" onClick={()=>copy(openapiUrl)}>Copy</Btn>
            <br/><strong>Auth:</strong> Bearer token (the <code>tk_...</code> key shown when generated)
            <br/><strong>Add at:</strong> <a href="https://claude.ai/settings/connectors" target="_blank" rel="noreferrer" style={{ color:G.cyan }}>claude.ai/settings/connectors</a>
          </div>
        )}
      </div>
    )}
  </div>;
}

function TollFreeVerificationCard({ toast }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [useCase, setUseCase] = useState('CUSTOMER_CARE');
  const [sampleMsg, setSampleMsg] = useState('');
  const [optInUrl, setOptInUrl] = useState('');

  const load = () => api('/api/settings/tollfree-verification').then(setData).catch(()=>{});
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const submit = async () => {
    if (!sampleMsg.trim()) return toast('Add at least one sample message', 'error');
    setBusy(true);
    try {
      await api('/api/settings/tollfree-verification', {
        method:'POST',
        body:{ useCase, sampleMessages: [sampleMsg.trim()], optInImageUrl: optInUrl.trim()||null, optInType: 'WEB_FORM' },
      });
      toast('Submitted to Twilio for review (1-3 day approval)', 'success');
      load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const statusColor = data.status === 'approved' ? G.green :
    data.status === 'pending_review' ? G.cyan :
    data.status === 'rejected' ? G.red : G.yellow;

  return <div style={{ ...styles.card, padding:18, marginBottom:18, borderColor:statusColor+'66' }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
      <span style={{ fontSize:22 }}>📱</span>
      <h3 style={{ margin:0 }}>Toll-Free Verification (A2P for +1-866 numbers)</h3>
      <Badge c={statusColor} bg={statusColor+'22'}>{data.status.replace('_',' ')}</Badge>
    </div>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:12, lineHeight:1.55 }}>
      Toll-free numbers (your `{data.twilioPhone || '+1-866-…'}`) need verification before carriers stop filtering messages.
      Approval is faster than full A2P 10DLC (1–3 business days vs 1–2 weeks). One-time submission per number.
    </div>

    {!data.canSubmit && <div style={{ padding:10, background:G.yellowLight, borderRadius:8, fontSize:13 }}>
      ⚠️ Complete your onboarding (Legal name + EIN + business address) before submitting.
    </div>}

    {data.status === 'not_submitted' && data.canSubmit && <div>
      <Field label="Use case category">
        <Select value={useCase} onChange={e=>setUseCase(e.target.value)}>
          <option value="CUSTOMER_CARE">Customer Care</option>
          <option value="2FA">2-Factor Auth / OTP</option>
          <option value="ACCOUNT_NOTIFICATION">Account Notifications</option>
          <option value="DELIVERY_NOTIFICATION">Delivery Notifications</option>
          <option value="MARKETING">Marketing</option>
          <option value="HIGHER_EDUCATION">Higher Education</option>
          <option value="POLLING_VOTING">Polling / Voting</option>
          <option value="POLITICAL">Political</option>
          <option value="PUBLIC_SERVICE_ANNOUNCEMENT">Public Service Announcement</option>
        </Select>
      </Field>
      <Field label="Sample message you'll send to customers" hint="Example: 'Hi {{first_name}}, this is Mike from Arctic HVAC confirming your appointment tomorrow at 2 PM. Reply STOP to opt out.'">
        <Textarea value={sampleMsg} onChange={e=>setSampleMsg(e.target.value)} placeholder="Hi John, confirming your AC service tomorrow at 2 PM. Reply STOP to opt out."/>
      </Field>
      <Field label="Opt-in screenshot URL (optional but speeds approval)" hint="A screenshot of where customers consent to receive texts — your contact form, booking form, or website checkbox.">
        <Input value={optInUrl} onChange={e=>setOptInUrl(e.target.value)} placeholder="https://yoursite.com/contact"/>
      </Field>
      <Btn v="primary" onClick={submit} disabled={busy}>{busy ? <Spinner/> : 'Submit for Verification'}</Btn>
    </div>}

    {data.status === 'pending_review' && <div style={{ padding:12, background:G.cyanLight || G.input, borderRadius:8, fontSize:13 }}>
      ⏳ Submitted{data.submittedAt ? ` ${new Date(data.submittedAt).toLocaleDateString()}` : ''}. Twilio typically approves within 1-3 business days. Until approved, you can text verified numbers only (add yourself + test numbers in Twilio Console).
    </div>}

    {data.status === 'approved' && <div style={{ padding:12, background:G.greenLight, borderRadius:8, fontSize:13, color:G.green }}>
      ✅ Verified — full sending allowed.
    </div>}

    {data.status === 'rejected' && <div style={{ padding:12, background:G.redLight, borderRadius:8, fontSize:13, color:G.red }}>
      ❌ Rejected. Re-submit with corrected info.
    </div>}
  </div>;
}

function SmsCompliancePanel({ toast }) {
  const [data, setData] = useState(null);
  const [phone, setPhone] = useState('');
  const load = () => api('/api/settings/sms-compliance').then(setData).catch(()=>{});
  useEffect(()=>{ load(); }, []);
  if (!data) return null;
  const s = data.status;
  const register = async () => {
    try { const r = await api('/api/settings/sms-compliance/register', { method:'POST' });
      toast(r.note || 'Registration started'); load();
    } catch(e) { toast(e.message, 'error'); }
  };
  const addOptOut = async () => {
    if (!phone) return;
    try { await api('/api/settings/sms-compliance/opt-out', { method:'POST', body:{ phone, reason:'Manual' } });
      setPhone(''); load(); toast('Opted out');
    } catch(e) { toast(e.message, 'error'); }
  };
  const reAdd = async (p) => {
    try { await api(`/api/settings/sms-compliance/opt-out/${encodeURIComponent(p)}`, { method:'DELETE' });
      load(); toast('Opted in');
    } catch(e) { toast(e.message, 'error'); }
  };
  const statusColor = s.status === 'approved' ? G.green : s.status === 'pending_review' ? G.cyan : s.status === 'unregistered' ? G.yellow : G.red;
  return <div style={{ marginTop:28 }}>
    <h3 style={{ margin:'18px 0 12px' }}>📜 SMS Compliance (10DLC + Opt-Outs)</h3>
    <div style={{ ...styles.card, padding:18, marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:14 }}>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.06em' }}>10DLC Campaign</div>
          <div style={{ marginTop:4 }}>
            Status: <Badge c={statusColor} bg={statusColor+'22'}>{s.status}</Badge>
          </div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:6 }}>
            {!s.canRegister ? 'Complete onboarding (Legal name + EIN) before registering.' :
              s.status === 'unregistered' ? 'Register now to send marketing SMS at scale without carrier filtering.' :
              s.status === 'pending_review' ? 'Submitted to The Campaign Registry — typically approved in 24-72 hours.' :
              'Campaign approved. You may send marketing SMS up to your tier limits.'}
          </div>
          <div style={{ fontSize:11, color:G.textDim, marginTop:6 }}>
            Disclaimer: <em>{s.disclaimer || 'none set'}</em>
          </div>
        </div>
        {s.status === 'unregistered' && s.canRegister && <Btn v="primary" onClick={register}>Register Campaign</Btn>}
      </div>
    </div>

    <div style={{ ...styles.card, padding:18 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700 }}>Opt-Out Registry</div>
          <div style={{ fontSize:12, color:G.textMuted }}>STOP keywords + manual additions. Outbound SMS skips these numbers automatically.</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Input placeholder="+15555550100" value={phone} onChange={e=>setPhone(e.target.value)} style={{ width:160 }}/>
          <Btn v="ghost" onClick={addOptOut}>Add</Btn>
        </div>
      </div>
      {data.optOuts.length === 0 ? <div style={{ fontSize:13, color:G.textMuted }}>No opt-outs yet.</div> :
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr auto', gap:8, fontSize:12 }}>
          <div style={{ fontWeight:700, color:G.textMuted }}>Phone</div>
          <div style={{ fontWeight:700, color:G.textMuted }}>Reason</div>
          <div style={{ fontWeight:700, color:G.textMuted }}>When</div>
          <div></div>
          {data.optOuts.map(o => <React.Fragment key={o.id}>
            <div>{o.phone}</div>
            <div style={{ color:G.textMuted }}>{o.reason}</div>
            <div style={{ color:G.textMuted }}>{new Date(o.opted_out_at).toLocaleString()}</div>
            <Btn sz="xs" v="ghost" onClick={()=>reAdd(o.phone)}>Re-opt-in</Btn>
          </React.Fragment>)}
        </div>}
    </div>
  </div>;
}

function BillingAuthModal({ open, onClose, billing, user, onAuthed }) {
  const [accept, setAccept] = useState(false);
  const [signature, setSignature] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!accept || !signature.trim()) return alert('Please accept terms and sign');
    setBusy(true);
    try { await api('/api/integrations/billing/authorize', { method:'POST', body:{ acceptTerms:true, signature: signature.trim() } }); onAuthed(); }
    catch(e) { alert(e.message); }
    finally { setBusy(false); }
  };
  const fee = (billing?.feeBpsByPlan?.[billing?.plan]||50)/100;
  const pd = billing?.plan_details || {};
  const r = billing?.rates || {};
  return <Modal open onClose={onClose} width={640}>
    <h2 style={{ margin:'0 0 14px 0' }}>🔐 Billing Authorization Agreement</h2>
    <div style={{ ...styles.card, padding:18, fontSize:13, lineHeight:1.6, maxHeight:380, overflow:'auto', background:G.input }}>
      <strong>Customer:</strong> {user?.account?.name || '—'} ({user?.email})<br/>
      <strong>Plan:</strong> {billing?.plan} {pd.monthlyUsd ? `($${pd.monthlyUsd}/mo)` : ''}<br/>
      {pd.includedSmsSegments != null && <>
        <strong>Included monthly:</strong> {pd.includedSmsSegments} SMS · {pd.includedAiVoiceMinutes} AI voice minutes · {pd.includedAiCreations} AI image/video creations<br/>
        <strong>Overage cap:</strong> ${pd.overageThresholdUsd || 50} (you'll be notified before this is reached)<br/>
      </>}
      <br/>
      By signing below, you authorize TradeOps to:
      <ol style={{ paddingLeft:20, marginTop:8 }}>
        <li>Collect a platform application fee of <strong>{fee}%</strong> on every customer payment you process through your connected Stripe account.</li>
        <li>Charge your saved payment method for: (a) the recurring monthly subscription, and (b) any metered overage AFTER your included monthly allowance is consumed, at the published rates below.</li>
        <li>Provision Twilio phone numbers under TradeOps' master Twilio account on your behalf.</li>
        <li>Sync invoice + payment data to QuickBooks and other connected services as you direct.</li>
      </ol>
      <div style={{ marginTop:10 }}>
        <strong>Per-unit overage rates (only charged after monthly allowance is used):</strong>
        <ul style={{ paddingLeft:20, marginTop:6 }}>
          <li>SMS: ${(r.sms_per_segment_usd||0.02).toFixed(2)} per segment</li>
          <li>AI voice: ${(r.voice_ai_per_min_usd||0.09).toFixed(2)} per minute</li>
          <li>AI image (standard): ${(r.image_standard_usd||0.01).toFixed(2)} each</li>
          <li>AI image (premium): ${(r.image_premium_usd||0.06).toFixed(2)} each</li>
          <li>AI video (short): ${(r.video_short_usd||0.10).toFixed(2)} each</li>
          <li>AI video (HD short): ${(r.video_hd_usd||0.16).toFixed(2)} each</li>
          <li>AI video (premium cinematic): ${(r.video_premium_usd||1.20).toFixed(2)} each</li>
        </ul>
      </div>
      <p style={{ marginTop:10 }}>You may revoke this authorization at any time by disconnecting integrations and cancelling your plan. Termination of authorization does not waive obligations for charges already incurred.</p>
    </div>
    <div style={{ marginTop:14 }}>
      <label style={{ display:'flex', alignItems:'flex-start', gap:10, fontSize:14, cursor:'pointer' }}>
        <input type="checkbox" checked={accept} onChange={e=>setAccept(e.target.checked)} style={{ marginTop:3 }}/>
        <span>I have read and accept the terms above. I am an authorized signer for {user?.account?.name||'this business'}.</span>
      </label>
    </div>
    <Field label="Type your full legal name to sign">
      <Input value={signature} onChange={e=>setSignature(e.target.value)} placeholder="Your full name" autoComplete="off"/>
    </Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={submit} disabled={busy || !accept || !signature.trim()}>{busy?<Spinner/>:'Sign & Authorize'}</Btn>
    </div>
  </Modal>;
}

// ─── QuickBooks 2-way Sync Panel ──────────────────────────────────────────
// Visible only when QB is connected. Lets the operator pull customers, invoices,
// and payments FROM QB into TradeOps so the two systems stay aligned without
// double-entry. Also shows recent sync events so failures are visible.
function QuickBooksSyncPanel({ toast }) {
  const [busy, setBusy] = useState(null);
  const [log, setLog] = useState([]);
  const loadLog = () => api("/api/integrations/quickbooks/sync-log?limit=10").then(setLog).catch(()=>{});
  useEffect(() => { loadLog(); }, []);
  const run = async (action, label) => {
    setBusy(action);
    try {
      const r = await api(`/api/integrations/quickbooks/${action}`, { method:"POST", body:{} });
      const summary = r.customers || r.invoices || r.payments
        ? `Customers: ${r.customers?.created||0}c/${r.customers?.updated||0}u · Invoices: ${r.invoices?.created||0}c/${r.invoices?.updated||0}u · Payments: ${r.payments?.reconciled||0} reconciled`
        : `${r.created||r.reconciled||0} added/reconciled · ${r.updated||0} updated · ${r.skipped||r.unmatched||0} skipped`;
      toast(`✓ ${label}: ${summary}`);
      loadLog();
    } catch(e) { toast(e.message, "error"); } finally { setBusy(null); }
  };
  return <div style={{ ...styles.card, padding:20, marginBottom:24, borderColor:G.green+"66" }}>
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:10 }}>
      <div>
        <h3 style={{ margin:0, fontSize:16 }}>📊 QuickBooks 2-way Sync</h3>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>
          Pull customers, invoices, and payments from QuickBooks into TradeOps. Safe to re-run — matches by QB ID so no duplicates.
        </div>
      </div>
    </div>
    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 }}>
      <Btn sz="sm" v="primary" disabled={!!busy} onClick={()=>run("full-sync", "Full sync")}>{busy==="full-sync"?<Spinner sz={14}/>:"↻ Full Sync (recommended)"}</Btn>
      <Btn sz="sm" disabled={!!busy} onClick={()=>run("pull-customers", "Pulled customers")}>{busy==="pull-customers"?<Spinner sz={14}/>:"👥 Pull Customers"}</Btn>
      <Btn sz="sm" disabled={!!busy} onClick={()=>run("pull-invoices", "Pulled invoices")}>{busy==="pull-invoices"?<Spinner sz={14}/>:"💰 Pull Invoices"}</Btn>
      <Btn sz="sm" disabled={!!busy} onClick={()=>run("pull-payments", "Reconciled payments")}>{busy==="pull-payments"?<Spinner sz={14}/>:"💳 Pull Payments"}</Btn>
    </div>
    {log.length > 0 && <div style={{ borderTop:`1px solid ${G.border}`, paddingTop:12 }}>
      <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:"uppercase", marginBottom:6 }}>Recent sync log</div>
      <div style={{ display:"grid", gap:4, maxHeight:200, overflowY:"auto" }}>
        {log.map(l => <div key={l.id} style={{ fontSize:12, color:l.error?G.red:G.textMuted, padding:"4px 8px", background:G.input, borderRadius:4 }}>
          <strong style={{ color:l.error?G.red:G.text }}>{l.direction} {l.kind}</strong> · {l.error || l.summary} <span style={{ color:G.textDim, marginLeft:4 }}>{new Date(l.created_at).toLocaleString()}</span>
        </div>)}
      </div>
    </div>}
  </div>;
}

// Super-admin only. Lists every platform-wide API key, shows whether each is
// set + where (env vs DB), and lets you paste in missing keys without touching
// Fly secrets. Reads prefer env (so fly-set secrets still win), DB is the
// fallback.
function PlatformSecretsCard({ toast }) {
  const [secrets, setSecrets] = useState(null);
  const [edits, setEdits] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = () => api('/api/admin-panel/platform-secrets').then(d => setSecrets(d.secrets || [])).catch(()=>setSecrets([]));
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const value = edits[key];
    if (value == null) return;
    setSavingKey(key);
    try {
      await api('/api/admin-panel/platform-secrets', { method:'PATCH', body:{ key, value: value.trim() }});
      toast(`Saved ${key}`, 'success');
      setEdits(e => { const n = { ...e }; delete n[key]; return n; });
      await load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setSavingKey(null); }
  };

  if (!secrets) return null;
  const missing = secrets.filter(s => !s.set);

  return <div style={{ ...styles.card, padding:20, marginBottom:24, borderColor: missing.length ? G.yellow+'66' : G.cardBorder }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }} onClick={()=>setExpanded(e=>!e)}>
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <h2 style={{ margin:0, fontSize:18 }}>Platform API Keys</h2>
          {missing.length > 0 ? <Badge c={G.yellow} bg={G.yellowLight}>{missing.length} missing</Badge>
            : <Badge c={G.green} bg={G.greenLight}><Dot c={G.green}/> All set</Badge>}
          <span style={{ fontSize:11, color:G.textMuted }}>(super-admin only)</span>
        </div>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>
          Platform-wide API keys (Google Ads, Maps, Anthropic, etc.). Set here OR via Fly secrets — env always wins. Values are never sent back to the browser.
        </div>
      </div>
      <span style={{ fontSize:18, color:G.textMuted }}>{expanded ? '▾' : '▸'}</span>
    </div>
    {expanded && <div style={{ marginTop:14, display:'grid', gap:10 }}>
      {secrets.map(s => {
        const editing = edits[s.key] !== undefined;
        return <div key={s.key} style={{ padding:'12px 14px', background:G.input, borderRadius:8, border:`1px solid ${s.set ? G.green+'33' : G.yellow+'44'}` }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
            <div style={{ flex:1, minWidth:220 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <strong style={{ fontSize:14 }}>{s.label}</strong>
                <code style={{ fontSize:11, color:G.textMuted }}>{s.key}</code>
                {s.set
                  ? <Badge c={G.green} bg={G.greenLight}>Set via {s.source === 'env' ? 'Fly secret' : 'DB'}</Badge>
                  : <Badge c={G.yellow} bg={G.yellowLight}>Not set</Badge>}
              </div>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{s.help}</div>
              {s.applyUrl && <a href={s.applyUrl} target="_blank" rel="noreferrer" style={{ color:G.purple, fontSize:11 }}>Apply / find key ↗</a>}
            </div>
            <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              {!editing ? <Btn sz="sm" v="ghost" onClick={()=>setEdits(e=>({...e, [s.key]: ''}))}>{s.set ? 'Replace' : 'Set value'}</Btn>
                : <>
                  <Input type="password" autoFocus placeholder="paste value" value={edits[s.key]} onChange={e=>setEdits(o=>({...o, [s.key]: e.target.value}))} style={{ minWidth:260 }}/>
                  <Btn sz="sm" v="primary" onClick={()=>save(s.key)} disabled={savingKey===s.key || !edits[s.key]?.trim()}>{savingKey===s.key ? <Spinner sz={12}/> : 'Save'}</Btn>
                  <Btn sz="sm" v="ghost" onClick={()=>setEdits(e=>{ const n={...e}; delete n[s.key]; return n; })}>Cancel</Btn>
                </>}
            </div>
          </div>
          {s.updatedAt && <div style={{ fontSize:10, color:G.textMuted, marginTop:6 }}>DB value updated {new Date(s.updatedAt).toLocaleString()}</div>}
        </div>;
      })}
    </div>}
  </div>;
}

